// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/system';
import { Typography, CircularProgress, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { deliverableCost, deliveryEstimate } from 'Utils/deliverables';
import TalkToSalesDialog from 'Components/TalkToSalesDialog';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'Store';
import { getOrders } from 'Features/order/orderSelectors';
import { ResetBundleStateAction, SelectedAdditionalDeliverablesAndLayersAction, SetDeliveryDateAction, SetDeliveryDaysAction } from 'Features/bundle/bundleActions';
import { projectTypes, utilitiesPackagesSubtypes } from 'Utils/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GetBundlesThunk, GetCustomBundleThunk } from 'Features/bundle/bundleThunk';
import BundleSelect from './BundleSelect';
import LayerSelect from './LayerSelect';
import DeliverableSelect from './DeliverableSelect';
import BundleButtons from './BundleButtons';
import stylesDef from './styles';

interface PackageProps {
  deliverySpeed: string;
  isSaved: boolean;
  checkFirstTimeChangeEstimate: (arg0: Function) => void;
  page: number;
  bundleStep: number;
  pricePerFoot: number;
  setPricePerFoot: (arg0: number) => void;
  setBundleStep: (bundleStep: number) => void;
  manageAddOns: (deliverableId: number, type: string, typeFactor: number, checked: boolean, typeFactor3D?: boolean) => void;
  addOnDeliverableDollars: number;
  addOnLayerDollars: number;
  zeroAddOns: () => void;
  setAllowTemplateReview: (arg0: boolean) => void;
  setCheckedForOverage: (arg0: boolean) => void;
  bundleType: string;
  setBundleType: (arg0: string) => void;
  bundleSubtype: string;
  setBundleSubtype: (arg0: string) => void;
}

const Package = ({ manageAddOns, deliverySpeed, isSaved, page, bundleStep, setBundleStep, addOnLayerDollars,
  zeroAddOns, checkFirstTimeChangeEstimate, addOnDeliverableDollars, setAllowTemplateReview, setCheckedForOverage,
  bundleType, setBundleType, bundleSubtype, setBundleSubtype, pricePerFoot, setPricePerFoot }: PackageProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { telecom } = useFlags();
  const [contactSalesInteraction, setContactSalesInteraction] = useState(false);
  const [availableProjectTypes, setAvailableProjectTypes] = useState(projectTypes);

  const { bundleDeliverables, additionalDeliverables, selectedBundle, selectedAdditionals, density, availableBundles, deliveryDays, loading, totalCost: bundleCost } = useAppSelector(
    (state: RootState) => state.bundle,
  );
  const { org } = useAppSelector(
    (state: RootState) => state.account,
  );

  const { availableCreditAcreage, costPerAcre, currentPricingPlanInfo } = org;
  const lowDensity = density === 'low';
  const pricingPlanName = currentPricingPlanInfo?.name;
  const { bundleId: currentBundleId, acreAdjustmentFactor } = selectedBundle || {};
  const activeOrder = useSelector((state: RootState) => getOrders(state).activeOrder);
  const placedOrder = useSelector((state: RootState) => getOrders(state).placedOrder);
  const [originalType, setOriginalType] = useState(bundleType);

  useEffect(() => {
    setOriginalType(bundleType);
  }, []);

  const expressAIBundle = currentBundleId === 9;
  const showOverageDialog = !expressAIBundle && !pricingPlanName && (!contactSalesInteraction && (availableCreditAcreage < activeOrder.acreage || (addOnDeliverableDollars + addOnLayerDollars) > 0));

  useEffect(() => {
    if (availableBundles?.length > 0 && !contactSalesInteraction && (availableCreditAcreage < activeOrder.acreage)) {
      const costB = availableCreditAcreage * costPerAcre + bundleCost;
      let orderAdjustedAcres = activeOrder.acreage;
      let addOnCredits = 0;

      if (activeOrder.isEstimate) {
        orderAdjustedAcres = activeOrder.acreage * acreAdjustmentFactor;

        if (activeOrder.addOns.length > 0 && additionalDeliverables.length > 0) {
          activeOrder.addOns.forEach((addOn) => {
            const additional = additionalDeliverables.find((deliverable: IDeliverable) => deliverable.name === addOn.name);
            const additionalTypeFactor = additional ? additional.typeFactor[0] : 0;
            const acreCost = deliverableCost(additionalTypeFactor, activeOrder.acreage);
            addOnCredits += acreCost;
          });
        }
      }

      const costA = (orderAdjustedAcres + addOnCredits) * costPerAcre;
      window?.pendo?.track('Out of Credits', {
        projectId: activeOrder?.ownerProject,
        orderAcreage: activeOrder?.acreage?.toFixed(2),
        costWithRenewing: costA?.toFixed(2),
        costWithoutRenewing: costB?.toFixed(2),
      });
    }
  }, [availableBundles, contactSalesInteraction, availableCreditAcreage, activeOrder.acreage]);

  useEffect(() => {
    if ((availableBundles?.length > 0)) {
      if (showOverageDialog) {
        setAllowTemplateReview(false);
      } else {
        setAllowTemplateReview(true);
        setCheckedForOverage(true);
      }
    }
  }, [availableBundles, showOverageDialog]);

  useEffect(() => {
    const checkCustomBundle = async () => {
      const response = await dispatch(GetCustomBundleThunk(activeOrder._id, activeOrder.ownerProject));
      if (response && response.deliverables?.bundles?.length > 0) {
        setAvailableProjectTypes(projectTypes);
      } else {
        setAvailableProjectTypes(projectTypes.filter((type) => type !== 'custom'));
      }
    }

    checkCustomBundle();
  }, []);

  const selectAdditionalDeliverable = (data: IDeliverable, checked: boolean, typeFactor3D?:boolean) => {
    let days = deliveryDays;
    const { deliverableId, type, time, typeFactor } = data;
    if (checked) {
      days += time;
      // When we add the first add-on to A la carte bundle, add 1.5 or 2 days depending on expedited or low density vs normal
      if (currentBundleId === 5 && selectedAdditionals.length === 0) {
        if (deliverySpeed === 'expedited' || lowDensity) days += 1.5;
        else if (deliverySpeed === 'super_expedited') days += 1;
        else days += 2;
      }
    } else {
      days -= time;
      if (currentBundleId === 5 && selectedAdditionals.length === 1) {
        if (deliverySpeed === 'expedited' || lowDensity) days -= 1.5;
        else if (deliverySpeed === 'super_expedited') days -= 1;
        else days -= 2;
      }
    }

    manageAddOns(deliverableId, type, typeFactor3D ? typeFactor[1] : typeFactor[0], checked);

    const calculateDate = deliveryEstimate(days);
    dispatch(SetDeliveryDaysAction(days));
    dispatch(SetDeliveryDateAction(calculateDate));
    dispatch(SelectedAdditionalDeliverablesAndLayersAction(typeFactor3D ? { ...data, is3D: true } : { ...data, is3D: false }));
  };

  const onAddOnsClick = (selectedList: { item: IDeliverable, checked: boolean, typeFactor3D?: boolean }[]) => {
    checkFirstTimeChangeEstimate(() => {
      selectedList.forEach((selectedItem) => {
        const { item, checked, typeFactor3D } = selectedItem;
        checkParentDependencies(item, checked);
        selectAdditionalDeliverable(item, checked, typeFactor3D);
      });
    });
  };

  const bundleOption = () => {
    switch (bundleStep) {
    case 0:
      return (<BundleSelect bundleType={bundleType} bundleSubtype={bundleSubtype} deliverySpeed={deliverySpeed} setBundleSubtype={setBundleSubtype} zeroAddOns={zeroAddOns} checkFirstTimeChangeEstimate={checkFirstTimeChangeEstimate} pricePerFoot={pricePerFoot} setPricePerFoot={setPricePerFoot} />);
    case 1:
      return (<DeliverableSelect onAddOnsClick={onAddOnsClick} />);
    case 2:
      return (<LayerSelect bundleType={bundleType} onAddOnsClick={onAddOnsClick} manageAddOns={manageAddOns} checkFirstTimeChangeEstimate={checkFirstTimeChangeEstimate} />);
    default:
      break;
    }
  };

  const checkParentDependencies = (item: any, checked: boolean) => {
    const _un: any[] = [];
    const _names: string[] = [];
    if (checked) {
      item.dependencies.forEach((dep: any) => {
        const dependencySelected = bundleDeliverables.some((del) => del._id === dep) || selectedAdditionals.some((del) => del._id === dep);
        if (!dependencySelected) {
          const deliverable = additionalDeliverables.find((b) => b._id === dep);
          _un.push(dep);
          _names.push(deliverable.name);
        }
      });
    } else {
      item.childDependencies.forEach((dep: any) => {
        const dependencySelected = bundleDeliverables.some((del) => del._id === dep) || selectedAdditionals.some((del) => del._id === dep);
        if (dependencySelected) {
          const deliverable = additionalDeliverables.find((b) => b._id === dep);
          _un.push(dep);
          _names.push(deliverable.name);
        }
      });
    }

    _un.forEach((dep) => {
      const deliverable = additionalDeliverables.find((del) => del._id === dep);
      selectAdditionalDeliverable(deliverable, checked);
    });
  };

  const contactSalesClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setContactSalesInteraction(true);
    window.open('mailto:sales@airworks.io', '_blank');
  };

  const handleBundleTypeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value;
    const subtype = newType === 'utilities' ? utilitiesPackagesSubtypes[0] : '';
    checkFirstTimeChangeEstimate(() => {
      zeroAddOns();
      setBundleStep(0);
      setBundleSubtype(subtype);
      setBundleType(newType);
      dispatch(ResetBundleStateAction());
      dispatch(GetBundlesThunk(activeOrder._id, activeOrder.ownerProject, newType, subtype, false));
    });
  };

  return (
    <Box sx={styles.packageColumn}>
      {isSaved && <Typography sx={styles.alert}>Alert: Any changes to your bundle or add-ons will invalidate your saved estimate.</Typography>}
      <Typography sx={styles.pageTitle} variant="h1">Select Base Package and Add-Ons</Typography>
      {telecom && (
        <RadioGroup
          sx={styles.radioGroup}
          value={bundleType}
          onChange={handleBundleTypeChange}
        >
          {availableProjectTypes.map((type) => <FormControlLabel sx={styles.typeText} value={type} control={<Radio />} label={type === 'standard' ? 'AEC' : type} disabled={!!placedOrder && (originalType !== type && type !== 'custom')}/>)}
        </RadioGroup>
      )}
      <Typography>
        {
          expressAIBundle ?
            'This base package is included in your plan and comes with every project. It generates imagery exclusively in 2D using Express AI. This feature ensures a quick and efficient creation process, allowing you to quickly visualize it in a 2D format. Please contact sales for questions or more info.'
            :
            'This is your base package that is included in your plan and comes with every project. You may choose another base package for this project; please note that any price differences will be adjusted in your usage at the end of the billing period. Please contact sales for questions or more info.'
        }
      </Typography>
      {(page === 0 && availableBundles?.length) ? (
        <Box>
          {bundleOption()}
          {!!additionalDeliverables.length && (
            <BundleButtons
              bundleStep={bundleStep}
              setBundleStep={setBundleStep}
              bundleType={bundleType}
            />
          )}
        </Box>
      ) : '' }
      {(page === 0 && loading) ? (
        <Box sx={styles.spinner}>
          <CircularProgress size={50} />
        </Box>
      ) : ''}
      {(availableBundles?.length > 0) && (
        <TalkToSalesDialog
          showDialog={showOverageDialog}
          onOk={contactSalesClick}
          onProceed={() => {
            setContactSalesInteraction(true);
          }}
          onCancel={() => {
            setContactSalesInteraction(true);
          }}
          addOnDeliverableDollars={addOnDeliverableDollars}
          addOnLayerDollars={addOnLayerDollars}
          titleText="Credit Limit Reached!"
        />
      )}
    </Box>
  );
};

export default Package;
